<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <grid
            ref="grid"
            :rowData="rowData"
            :columnDefs="columnDefs"
            gridTheme="ag-theme-alpine-report"
            :pinnedBottomRowData="pinnedBottomRowData"
            :getRowStyle="rowStyle"
            :isSearch="false"
            :isAutoWidth="true"
            :suppressColumnVirtualisation="false"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <b-button-group class="mr-1 d-lg-flex d-none">
                <properties-search-multiselect
                    v-model="propertiesSelected"
                    variant="primary"
                    :properties="properties"
                ></properties-search-multiselect>
            </b-button-group>
            <export-to-excel-button
                :href="excelHref"
                :on-too-many-records="showLimitReportPeriodErrorToast"
            />
            <export-to-pdf-button
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
                tableFontSize="sm"
                :on-too-many-records="showLimitReportPeriodErrorToast"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="orientationFileOption"
                variant="primary"
                >{{ orientationFileLabel }}
            </responsive-dropdown>
            <b-button-group>
                <more-filters-dropdown class="arrivals-dropdown">
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <b-button-group class="mr-1 d-lg-none d-flex">
                            <properties-search-multiselect
                                v-model="propertiesSelected"
                                variant="primary"
                                :properties="properties"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            ></properties-search-multiselect>
                        </b-button-group>

                        <form-square-switch
                            v-for="filter in arrivalsFilters"
                            :key="filter.key"
                            :labels="{
                                checked: filter.labels,
                                unchecked: filter.labels,
                            }"
                            v-model="filterValues[filter.key]"
                            :tag="filter.key"
                            @change="handleFiltersChange"
                            class="mb-1 arrivals-toggle"
                            :sync="true"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                class="ml-1"
            />
        </grid>
    </page>
</template>

<script>
import format from 'date-fns/format';
import API from '../../api';
import {
    REPORT_ARRIVALS_ENDPOINT,
    REPORT_ARRIVALS_EXCEL_ENDPOINT,
    REPORT_ARRIVALS_PROPERTIES_ENDPOINT,
} from '../../endpoints';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    NEXT_MONTH,
    NEXT_WEEK,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    TOMORROW,
    YESTERDAY,
} from '../generic/date-range-picker/date-range-picker.vue';
import Grid from '../generic/grid/grid.vue';
import page from '../generic/page/page.vue';
import PropertiesSearchMultiselect from '../generic/properties-search-multiselect/properties-search-multiselect.vue';
import { mapGetters } from 'vuex';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import { isMediumScreen } from '../../utils/screens';
import { escapeHTML } from '../../utils/strings';
import ReportSettingsModal, {
    PROPERTIES_MULTI_SELECT,
    SWITCH_INPUT,
    DATE_PERIODS_INPUT,
} from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const exportToPdfButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    components: {
        page,
        Grid,
        PropertiesSearchMultiselect,
        DateRangePicker,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        FormSquareSwitch,
        ExportToExcelButton,
        exportToPdfButton,
        ReportSettingsModal,
    },
    name: 'ArrivalsReport',
    data() {
        const defaultShowDepartures =
            this.$store.getters['arrivalLocalStorage/showDepartures'];
        const defaultShowDisabledProperties =
            this.$store.getters['arrivalLocalStorage/showDisabledProperties'];
        const defaultShowOwnerBlocks =
            this.$store.getters['arrivalLocalStorage/showOwnerBlocks'];
        const defaultShowSubscriberBlocks =
            this.$store.getters['arrivalLocalStorage/showSubscriberBlocks'];
        const defaultShowNotes =
            this.$store.getters['arrivalLocalStorage/showNotes'];
        const defaultProperties =
            this.$store.getters['arrivalLocalStorage/properties'];
        return {
            busy: false,
            rowData: [],
            tabs: [
                {
                    title: 'Arrival Report',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            gridApi: {},
            landscapeOrientationFileLabel: 'Landscape',
            orientationFileLabel: 'Landscape',
            portraitOrientationFileLabel: 'Portrait',
            pageOrientation: 'landscape',
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                LAST_YEAR,
            ],
            properties: [],
            propertiesSelected: defaultProperties,
            rowStyle: null,
            filterValues: {
                showDepartures: defaultShowDepartures,
                showDisabledProperties: defaultShowDisabledProperties,
                showOwnerBlocks: defaultShowOwnerBlocks,
                showSubscriberBlocks: defaultShowSubscriberBlocks,
                showNotes: defaultShowNotes,
            },
            total: null,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        pinnedBottomRowData() {
            return [
                {
                    id: this.filteredRows,
                    guests: this.totalGuests,
                },
            ];
        },
        columnDefs() {
            return [
                {
                    headerName: '#',
                    field: 'id',
                    maxWidth: 60,
                    minWidth: 60,
                    sortable: false,
                    colSpan: (params) => (params.node.rowPinned ? 3 : 1),
                    valueGetter: (params) => {
                        return params.node.rowPinned
                            ? this.filteredRows && this.filteredRows.length
                            : params.node.rowIndex + 1;
                    },
                },
                {
                    field: 'name',
                    headerName: 'Guest Name',
                },
                {
                    field: 'phone_number',
                    headerName: 'Guest Phone',
                    valueGetter: (params) =>
                        !params.node.rowPinned
                            ? params.data.phone_number || 'N/A'
                            : '',
                },
                {
                    field: 'check_in',
                    headerName: 'Arriving',
                    type: 'dateTimeColumn',
                },
                {
                    field: 'check_out',
                    headerName: 'Depart',
                    type: 'dateTimeColumn',
                },
                {
                    field: 'rental_property.name',
                    headerName: 'Property',
                    minWidth: 160,
                },
                {
                    field: 'room',
                    headerName: 'Unit #',
                    valueGetter: (params) =>
                        !params.node.rowPinned ? params.data.room || 'N/A' : '',
                    hide: this.isHideUnitColumn,
                },
                {
                    field: 'nights',
                    headerName: 'Nights',
                },
                {
                    field: 'guests',
                    headerName: 'Guests',
                },
                {
                    field: 'cleaner_names',
                    headerName: 'Cleaner',
                    minWidth: 100,
                },
                {
                    headerName: 'Notes',
                    autoHeight: true,
                    minWidth: 300,
                    sortable: false,
                    autoWidthExclude: true,
                    cellRenderer: ({ data }) => {
                        return (
                            data &&
                            data.notes &&
                            data.notes
                                .map((item) => {
                                    return `<b>${escapeHTML(
                                        item.title
                                    )}</b> ${escapeHTML(item.note)}`;
                                })
                                .join('<br/>')
                        );
                    },
                    wrapText: true,
                },
            ];
        },
        exportPdfHref() {
            let url = '/app/reports/arrivals/report/pdf/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.propertiesRequest}`;
            url += `&show_departures=${this.filterValues.showDepartures}`;
            url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
            url += `&show_blocks=${this.filterValues.showOwnerBlocks}`;
            url += `&show_subscriber_blocks=${this.filterValues.showSubscriberBlocks}`;
            url += `&show_notes=${this.filterValues.showNotes}`;
            return url;
        },
        excelHref() {
            let url = REPORT_ARRIVALS_EXCEL_ENDPOINT();
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.propertiesRequest}`;
            url += `&show_departures=${this.filterValues.showDepartures}`;
            url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
            url += `&show_blocks=${this.filterValues.showOwnerBlocks}`;
            url += `&show_subscriber_blocks=${this.filterValues.showSubscriberBlocks}`;
            url += `&show_notes=${this.filterValues.showNotes}`;
            return url;
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        reportSettings() {
            return {
                name: 'Arrivals Report',
                report_sysname: 'ARRIVALS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date Range',
                        name: 'date_range',
                        predefinedRanges: [
                            YESTERDAY,
                            TODAY,
                            TOMORROW,
                            LAST_WEEK,
                            THIS_WEEK,
                            NEXT_WEEK,
                            LAST_MONTH,
                            THIS_MONTH,
                            NEXT_MONTH,
                            THIS_YEAR,
                            LAST_YEAR,
                        ],
                    },
                    {
                        type: PROPERTIES_MULTI_SELECT,
                        options: this.properties,
                        name: 'properties',
                        value: [],
                        label: 'Only For Properties',
                        placeholder: 'All Properties'
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Owner/iCal Blocks',
                        name: 'show_blocks',
                        value: false,
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Subscriber Blocks',
                        name: 'show_subscriber_blocks',
                        value: false,
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Disabled Properties',
                        name: 'show_disabled_properties',
                        value: false,
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Departures',
                        name: 'show_departures',
                        value: false,
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Invoice Notes',
                        name: 'show_notes',
                        value: false,
                    },
                ],
            };
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        totalGuests() {
            return (
                this.total &&
                this.total.guests &&
                `${this.total.adults}/${this.total.children}/${this.total.guests}`
            );
        },
        orientationFileOption() {
            return [
                {
                    label: this.landscapeOrientationFileLabel,
                    click: () => {
                        this.pageOrientation = 'landscape';
                        this.orientationFileLabel =
                            this.landscapeOrientationFileLabel;
                    },
                },
                {
                    label: this.portraitOrientationFileLabel,
                    click: () => {
                        this.pageOrientation = 'portrait';
                        this.orientationFileLabel =
                            this.portraitOrientationFileLabel;
                    },
                },
            ];
        },
        filteredRows() {
            return (
                this.gridApi &&
                this.gridApi?.rowModel?.rootNode?.childrenAfterFilter.filter(
                    (item) => {
                        return !item.data.is_departure;
                    }
                )
            );
        },
        propertiesRequest() {
            return this.propertiesSelected
                .map((property) => property.property_id)
                .join(',');
        },
        arrivalsFilters() {
            return [
                {
                    labels: 'Show Owner/iCal Blocks',
                    key: `showOwnerBlocks`,
                    cache: (value) =>
                        this.$store.dispatch(
                            'arrivalLocalStorage/changeShowOwnerBlocks',
                            value
                        ),
                },
                {
                    labels: 'Show Subscriber Blocks',
                    key: `showSubscriberBlocks`,
                    cache: (value) =>
                        this.$store.dispatch(
                            'arrivalLocalStorage/changeShowSubscriberBlocks',
                            value
                        ),
                },
                {
                    labels: 'Show Disabled Properties',
                    key: `showDisabledProperties`,
                    cache: (value) =>
                        this.$store.dispatch(
                            'arrivalLocalStorage/changeShowDisabledProperties',
                            value
                        ),
                },
                {
                    labels: 'Show Departures',
                    key: `showDepartures`,
                    cache: (value) =>
                        this.$store.dispatch(
                            'arrivalLocalStorage/changeShowDepartures',
                            value
                        ),
                },
                {
                    labels: 'Show Invoice Notes',
                    key: `showNotes`,
                    cache: (value) =>
                        this.$store.dispatch(
                            'arrivalLocalStorage/changeShowNotes',
                            value
                        ),
                },
            ];
        },
        isMobile() {
            return !this.isMediumScreen();
        },
        isHideUnitColumn() {
            return this.rowData.every((item) => !item.room);
        },
    },
    mounted() {
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        (async () => {
            this.busy = true;
            try {
                await Promise.all([
                    this.fetchArrivals(),
                    this.fetchProperties(),
                ]);
            } finally {
                this.busy = false;
            }
        })();
    },
    methods: {
        isMediumScreen,
        async fetchArrivals() {
            try {
                const { data } = await API.get(REPORT_ARRIVALS_ENDPOINT(), {
                    params: {
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        properties: this.propertiesRequest,
                        show_departures: this.filterValues.showDepartures,
                        show_disabled_properties:
                            this.filterValues.showDisabledProperties,
                        show_blocks: this.filterValues.showOwnerBlocks,
                        show_subscriber_blocks:
                            this.filterValues.showSubscriberBlocks,
                        show_notes: this.filterValues.showNotes,
                    },
                });
                this.pdfCacheKey = data.pdf_cache_key;
                this.rowData = data.arrivals;
                this.total = data.total;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch arrivals' });
            }
        },
        async fetchProperties() {
            try {
                const { data } = await API.get(
                    REPORT_ARRIVALS_PROPERTIES_ENDPOINT()
                );
                this.properties = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch arrivals' });
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        async refresh() {
            this.busy = true;
            try {
                await this.fetchArrivals();
            } finally {
                this.busy = false;
            }
        },
        showLimitReportPeriodErrorToast() {
            this.limitReportPeriodErrorToast({
                currentStartDate: this.dateRange.from,
                currentEndDate: this.dateRange.to,
            });
        },
        handleFiltersChange({ tag, value }) {
            this.arrivalsFilters
                .find((filter) => filter.key === tag)
                .cache(value);
            this.refresh();
        },
    },
    watch: {
        propertiesSelected(properties) {
            this.$store.dispatch(
                'arrivalLocalStorage/changeProperties',
                properties
            );
            this.refresh();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.arrivals-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }

    .arrivals-toggle {
        width: 200px;
        @include media-breakpoint-down(lg) {
            width: 220px;
        }
    }
}
</style>